import * as React from 'react';
import { ChevronDownSmall as ChevronDown } from '@wix/wix-ui-icons-common/on-stage';
import { TextButton, TextButtonPriority } from '../../TextButton';
import { classes, st } from './ReactionButton.st.css';
import { ReactionsDataHooks } from '../dataHooks';
const TIMER_DELAY = 500;
export const ReactionButton = React.forwardRef(({ className, selectedReaction, active, onTriggerReaction, onTriggerPopover, popoverTriggerAriaLabel, mobile, expanded, }, ref) => {
    const [animate, setAnimate] = React.useState(false);
    const [isTriggerVisible, setTriggerVisibility] = React.useState(false);
    const subTriggerRef = React.useRef();
    const triggerTimer = React.useRef();
    const documentListenerTimer = React.useRef();
    const isPressedKey = React.useRef(false);
    const rootRef = React.useRef();
    const animationFrame = React.useRef();
    const doesMainButtonTriggerPopover = mobile && !active;
    const ariaProps = doesMainButtonTriggerPopover
        ? {
            'aria-haspopup': 'menu',
            'aria-expanded': expanded,
        }
        : {
            'aria-pressed': active,
        };
    const clearTimer = () => clearTimeout(triggerTimer.current);
    React.useImperativeHandle(ref, () => ({
        cancelPopover: clearTimer,
        focus: () => subTriggerRef.current?.focus(),
    }), []);
    const onAnimationEnd = () => {
        setAnimate(false);
    };
    const showTriggerButton = () => {
        setTriggerVisibility(true);
    };
    const hideTriggerButton = () => {
        // Don't hide if popover is open
        if (!expanded) {
            setTriggerVisibility(false);
        }
    };
    const onFocus = () => {
        if (isPressedKey.current) {
            cancelAnimationFrame(animationFrame.current);
            showTriggerButton();
        }
    };
    const onBlur = () => {
        if (isPressedKey.current &&
            !rootRef.current.contains(document.activeElement)) {
            animationFrame.current = requestAnimationFrame(() => {
                hideTriggerButton();
            });
        }
    };
    const onClick = (e) => {
        if (doesMainButtonTriggerPopover) {
            onTriggerPopover();
        }
        else {
            // prevent focus on button
            e.preventDefault();
            onTriggerReaction();
        }
    };
    const onMouseEnter = () => {
        triggerTimer.current = setTimeout(onTriggerPopover, TIMER_DELAY);
    };
    const onDocumentKeyDown = () => {
        isPressedKey.current = true;
        documentListenerTimer.current = requestAnimationFrame(() => (isPressedKey.current = false));
    };
    React.useEffect(() => {
        if (active) {
            setAnimate(true);
        }
    }, [active, selectedReaction.id]);
    React.useEffect(() => {
        document.addEventListener('keydown', onDocumentKeyDown);
        return () => {
            clearTimer();
            cancelAnimationFrame(documentListenerTimer.current);
            document.removeEventListener('keydown', onDocumentKeyDown);
        };
    }, []);
    return (React.createElement("div", { onFocus: !mobile ? onFocus : undefined, onBlur: !mobile ? onBlur : undefined, ref: rootRef, className: st(classes.root, {
            defaultReaction: selectedReaction.id,
            active,
            animate,
            visibleTrigger: isTriggerVisible,
        }, className) },
        React.createElement(TextButton, { className: classes.reactionButton, onClick: onClick, onMouseEnter: !mobile ? onMouseEnter : undefined, onMouseLeave: !mobile ? clearTimer : undefined, ...ariaProps, prefixIcon: React.cloneElement(selectedReaction.icon, {
                onAnimationEnd,
            }), priority: TextButtonPriority.secondary, "data-hook": `${ReactionsDataHooks.TriggerButton} ${active ? ReactionsDataHooks.HasReaction : ''}` },
            React.createElement("span", { "data-hook": ReactionsDataHooks.ReactionLabel }, selectedReaction.label)),
        !mobile ? (React.createElement("button", { className: classes.popoverTrigger, onClick: onTriggerPopover, ref: subTriggerRef, "aria-label": popoverTriggerAriaLabel, "aria-haspopup": "menu", "aria-expanded": expanded, "data-hook": ReactionsDataHooks.SubTriggerButton },
            React.createElement(ChevronDown, null))) : null));
});
//# sourceMappingURL=ReactionButton.js.map